/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Dispatch, StateUpdater, useRef } from 'preact/hooks';
import type { CommonDndProps } from '#utils/UNSAFE_visTypes/common';

export type DragImageProps<DragStartDetail> = {
  event: DragEvent;
  target: HTMLElement;
  context: DragStartDetail;
};

type Props<DragStartDetail, DropDetail> = {
  getDragContext: (target: HTMLElement, event: DragEvent) => DragStartDetail;
  getDragImage: (props: DragImageProps<DragStartDetail>) => {
    image: HTMLElement | SVGElement;
    offSetX: number;
    offSetY: number;
    width: number;
    height: number;
  } | null;
  getDropContext: (event: DragEvent) => DropDetail;
  onPreDndStart?: (detail: DragStartDetail) => void;
  setDatatipDisabled: Dispatch<StateUpdater<boolean>>;
  showDropEffect?: (context: DropDetail) => void;
  showRejectedDropEffect?: (event: DropDetail) => void;
  clearDropEffect?: () => void;
} & CommonDndProps<DragStartDetail, DropDetail>;

export const useDnd = <DragStartDetail, DropDetail>({
  onDragStart,
  onDrag,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  getDragContext,
  getDropContext,
  getDragImage,
  onPreDndStart,
  setDatatipDisabled,
  showDropEffect = () => undefined,
  showRejectedDropEffect = () => undefined,
  clearDropEffect = () => undefined
}: Props<DragStartDetail, DropDetail>) => {
  const dragCandidate = useRef<HTMLElement | null>(null);
  const dragImageRef = useRef<HTMLDivElement>(null);
  const dndContent = <div ref={dragImageRef}></div>;
  const onPointerDown = (event: PointerEvent) => {
    dragCandidate.current = event.target as HTMLElement;
  };

  const onDndStart = (event: DragEvent) => {
    const target = dragCandidate.current ? dragCandidate.current : (event.target as HTMLElement);
    const context = getDragContext(target, event);
    if (context) {
      onPreDndStart && onPreDndStart(context);
      setDatatipDisabled(true);
      const dragImg = getDragImage({ event, target, context });
      if (!dragImg) return;
      const dragImage = dragImg.image;
      dragImage.style.width = dragImg.width + 'px';
      dragImage.style.height = dragImg.height + 'px';
      // JET-69400 Drag and drop multiple nodes far apart shows wrong drag image
      dragImage.style.left = -1 * dragImg.width + 'px';
      dragImage.style.top = -1 * dragImg.height + 'px';
      dragImage.style.position = 'absolute';
      document.body.append(dragImage); // @HTMLUpdateOK will remove in next animation frame
      event.dataTransfer?.setDragImage(dragImage, dragImg.offSetX, dragImg.offSetY);
      window.requestAnimationFrame(() => {
        document.body.removeChild(dragImage);
      });
      onDragStart && onDragStart(context);
    } else {
      event.preventDefault();
    }
  };

  const onDndDrag = (event: DragEvent) => {
    onDrag && onDrag({ event: event });
  };

  const onDndEnd = (event: DragEvent) => {
    setDatatipDisabled(false);
    onDragEnd && onDragEnd({ event: event });
  };

  const onDndEnter = (event: DragEvent) => {
    const context = getDropContext(event);
    if (onDragEnter && context) {
      onDragEnter(context);
      if (event.defaultPrevented) {
        showDropEffect(context);
      } else {
        showRejectedDropEffect(context);
      }
    }
  };

  const onDndLeave = (event: DragEvent) => {
    const context = getDropContext(event) as DropDetail;
    onDragLeave && context && onDragLeave(context);
    clearDropEffect();
  };

  const onDndOver = (event: DragEvent) => {
    const context = getDropContext(event) as DropDetail;
    if (onDragOver && context) {
      onDragOver(context);
      if (event.defaultPrevented) {
        showDropEffect(context);
      } else {
        showRejectedDropEffect(context);
      }
    }
  };

  const onDndDrop = (event: DragEvent) => {
    const context = getDropContext(event) as DropDetail;
    onDrop && context && onDrop(context);
    clearDropEffect();
  };

  return {
    dragProps: {
      onDragStart: onDndStart,
      onDrag: onDndDrag,
      onDragEnd: onDndEnd,
      onPointerDown
    },
    dropProps: {
      onDragEnter: onDndEnter,
      onDragLeave: onDndLeave,
      onDragOver: onDndOver,
      onDrop: onDndDrop
    },
    content: dndContent
  };
};
